import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  Chip
} from '@mui/joy';
import PageLayout from '../components/PageLayout';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';
import { supabase } from '../supabaseClient';

import UserManager from './admin/UserManager';
import RoleManager from './admin/RoleManager';
import ClientManager from './admin/ClientManager';
import ProjectManager from './admin/ProjectManager';
import ProjectTemplateManager from './admin/ProjectTemplateManager';
import GISProjectsAdmin from './admin/GISProjectsAdmin';
import CalendarAdmin from './admin/CalendarAdmin';
import AnnouncementAdmin from './admin/AnnouncementAdmin';
import LinksAdmin from './admin/LinksAdmin';
import Auditlog from './admin/Auditlog';

const Admin = () => {
  const [activePanel, setActivePanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminStats, setAdminStats] = useState({
    totalUsers: 0,
    totalClients: 0,
    totalProjects: 0,
    totalGISProjects: 0
  });

  useEffect(() => {
    const fetchAdminStats = async () => {
      setLoading(true);
      try {
        const [usersRes, clientsRes, projectsRes, gisRes] = await Promise.all([
          supabase.from('users').select('id'),
          supabase.from('clients').select('id'),
          supabase.from('projects').select('id'),
          supabase.from('gis_projects').select('id')
        ]);

        setAdminStats({
          totalUsers: usersRes.data?.length || 0,
          totalClients: clientsRes.data?.length || 0,
          totalProjects: projectsRes.data?.length || 0,
          totalGISProjects: gisRes.data?.length || 0
        });
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      }
      setLoading(false);
    };

    fetchAdminStats();
  }, []);

  const adminModules = [
    { 
      title: 'User Management', 
      icon: 'group', 
      component: <UserManager />,
      description: 'Manage users, roles, and permissions',
      gradientColors: ['#00A884', '#007E33']
    },
    { 
      title: 'Client Management', 
      icon: 'business', 
      component: <ClientManager />,
      description: 'Add, edit, and organize clients',
      gradientColors: ['#17a2b8', '#138496']
    },
    { 
      title: 'Project Management', 
      icon: 'folder', 
      component: <ProjectManager />,
      description: 'Oversee all project activities',
      gradientColors: ['#28a745', '#218838']
    },
    { 
      title: 'GIS Projects', 
      icon: 'map', 
      component: <GISProjectsAdmin />,
      description: 'Manage GIS mapping projects',
      gradientColors: ['#6c757d', '#5a6268']
    },
    { 
      title: 'Project Templates', 
      icon: 'list_alt', 
      component: <ProjectTemplateManager />,
      description: 'Create and manage project templates',
      gradientColors: ['#fd7e14', '#e8570c']
    },
    { 
      title: 'Calendar Events', 
      icon: 'event', 
      component: <CalendarAdmin />,
      description: 'Schedule and manage events',
      gradientColors: ['#6f42c1', '#59359a']
    },
    { 
      title: 'Announcements', 
      icon: 'campaign', 
      component: <AnnouncementAdmin />,
      description: 'Create system-wide announcements',
      gradientColors: ['#dc3545', '#c82333']
    },
    { 
      title: 'Quick Links', 
      icon: 'link', 
      component: <LinksAdmin />,
      description: 'Manage navigation quick links',
      gradientColors: ['#20c997', '#1ba085']
    },
    { 
      title: 'Role Management', 
      icon: 'security', 
      component: <RoleManager />,
      description: 'Configure user roles and access',
      gradientColors: ['#ffc107', '#e0a800']
    },
    { 
      title: 'Audit Log', 
      icon: 'history', 
      component: <Auditlog />,
      description: 'View system activity and changes',
      gradientColors: ['#495057', '#343a40']
    }
  ];

  const handlePanelClose = () => {
    setActivePanel(null);
  };

  if (loading) {
    return (
      <PageLayout title="Admin Dashboard" fullWidth>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          {/* Analytics Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="300px" sx={{ mb: 3 }} />
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
              <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            </Grid>
          </Box>
          
          {/* Modules Loading */}
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              {[...Array(10)].map((_, i) => (
                <Grid key={i} xs={12} sm={6} md={4} lg={3}><CardSkeleton /></Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin Dashboard" fullWidth>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        {/* Breadcrumb Navigation */}
        {activePanel && (
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs
              sx={{
                bgcolor: 'success.25',
                p: 2,
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'success.200'
              }}
              className="fade-in"
            >
              <Link 
                component="button"
                onClick={handlePanelClose}
                sx={{ 
                  color: 'success.600',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Admin Dashboard
              </Link>
              <Typography sx={{ color: 'success.700' }}>{activePanel}</Typography>
            </Breadcrumbs>
          </Box>
        )}

        {!activePanel && (
          <>
            {/* Admin Analytics Overview */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                level="h3" 
                sx={{ 
                  mb: 3, 
                  color: 'success.700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
                className="fade-in"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
                  admin_panel_settings
                </span>
                System Overview
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
                <Grid xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Total Users"
                    value={adminStats.totalUsers}
                    icon="group"
                    gradientColors={['#00A884', '#007E33']}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Active Clients"
                    value={adminStats.totalClients}
                    icon="business"
                    gradientColors={['#17a2b8', '#138496']}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Projects"
                    value={adminStats.totalProjects}
                    icon="folder"
                    gradientColors={['#28a745', '#218838']}
                  />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <MetricCard
                    title="GIS Projects"
                    value={adminStats.totalGISProjects}
                    icon="map"
                    gradientColors={['#6c757d', '#5a6268']}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Admin Modules */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                level="h4" 
                sx={{ 
                  mb: 3, 
                  color: 'success.700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
                className="fade-in"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  dashboard
                </span>
                Administration Modules
              </Typography>
              
              <Grid container spacing={3} className="fade-in-up">
                {adminModules.map((module) => (
                  <Grid key={module.title} xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: `linear-gradient(135deg, ${module.gradientColors[0]}10 0%, ${module.gradientColors[1]}05 100%)`,
                        border: '1px solid',
                        borderColor: 'neutral.200',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 25px ${module.gradientColors[0]}25`,
                          borderColor: module.gradientColors[0]
                        }
                      }}
                      className="hover-lift"
                      onClick={() => setActivePanel(module.title)}
                    >
                      {/* Module Header */}
                      <Box 
                        sx={{ 
                          p: 3, 
                          textAlign: 'center',
                          background: `linear-gradient(135deg, ${module.gradientColors[0]}15 0%, ${module.gradientColors[1]}10 100%)`,
                          borderBottom: '1px solid',
                          borderColor: 'neutral.200'
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            width: 56,
                            height: 56,
                            mx: 'auto',
                            mb: 2,
                            background: `linear-gradient(135deg, ${module.gradientColors[0]} 0%, ${module.gradientColors[1]} 100%)`,
                            color: 'white'
                          }}
                        >
                          <span 
                            className="material-symbols-outlined" 
                            style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}
                          >
                            {module.icon}
                          </span>
                        </Avatar>
                        
                        <Typography 
                          level="title-md" 
                          sx={{ 
                            color: 'success.700',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {module.title}
                        </Typography>
                        
                        <Typography 
                          level="body-sm" 
                          sx={{ 
                            color: 'text.secondary',
                            mb: 2
                          }}
                        >
                          {module.description}
                        </Typography>
                        
                        <Chip 
                          size="sm" 
                          variant="soft" 
                          sx={{
                            bgcolor: `${module.gradientColors[0]}20`,
                            color: module.gradientColors[0],
                            fontWeight: 500
                          }}
                        >
                          Admin Module
                        </Chip>
                      </Box>

                      {/* Action Area */}
                      <Box sx={{ p: 2 }}>
                        <EnhancedButton
                          fullWidth
                          size="sm"
                          variant="outlined"
                          sx={{
                            borderColor: module.gradientColors[0],
                            color: module.gradientColors[0],
                            '&:hover': {
                              bgcolor: `${module.gradientColors[0]}10`,
                              borderColor: module.gradientColors[1]
                            }
                          }}
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                              open_in_new
                            </span>
                          }
                        >
                          Open Module
                        </EnhancedButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Quick Actions */}
            <Card 
              sx={{ 
                p: 3,
                background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
                border: '1px solid',
                borderColor: 'success.200',
              }}
              className="fade-in"
            >
              <Typography 
                level="h4" 
                sx={{ 
                  mb: 2, 
                  color: 'success.700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                  flash_on
                </span>
                Quick Access
              </Typography>
              
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                Commonly used administrative functions for efficient system management
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <EnhancedButton
                  size="sm"
                  onClick={() => setActivePanel('User Management')}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                      person_add
                    </span>
                  }
                >
                  Add New User
                </EnhancedButton>
                <EnhancedButton
                  size="sm"
                  variant="outlined"
                  onClick={() => setActivePanel('Client Management')}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                      business
                    </span>
                  }
                >
                  Manage Clients
                </EnhancedButton>
                <EnhancedButton
                  size="sm"
                  variant="outlined"
                  onClick={() => setActivePanel('Audit Log')}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                      history
                    </span>
                  }
                >
                  View Activity
                </EnhancedButton>
              </Box>
            </Card>
          </>
        )}

        {/* Active Panel Content */}
        {activePanel && (
          <Box className="fade-in">
            <Card sx={{ overflow: 'hidden' }}>
              <Box sx={{ p: 3, bgcolor: 'success.50', borderBottom: '1px solid', borderColor: 'success.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography 
                    level="h4" 
                    sx={{ 
                      color: 'success.700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                      {adminModules.find(m => m.title === activePanel)?.icon}
                    </span>
                    {activePanel}
                  </Typography>
                  <EnhancedButton
                    variant="outlined"
                    size="sm"
                    onClick={handlePanelClose}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                        close
                      </span>
                    }
                  >
                    Close
                  </EnhancedButton>
                </Box>
              </Box>
              
              <Box>
                {adminModules.map(({ title, component }) => activePanel === title && (
                  <Box key={title}>{component}</Box>
                ))}
              </Box>
            </Card>
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};

export default Admin;